import { Flex, IconButton, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { IconType } from 'react-icons';

export interface RowAction {
    icon: IconType;
    label: string;
    onClick: () => void;
    colorScheme?: string;
}

interface TableRowActionsProps {
    actions: RowAction[];
}

/**
 * Hover-to-reveal action buttons for table rows.
 * Wrap the row with role="group" and place this in the last <Td>.
 *
 * Usage:
 *   <Tr role="group" _hover={{ bg: 'brand.50' }}>
 *     ...cells...
 *     <Td><TableRowActions actions={[{ icon: MdEdit, label: 'Edit', onClick: ... }]} /></Td>
 *   </Tr>
 */
export default function TableRowActions({ actions }: TableRowActionsProps) {
    const hoverBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

    return (
        <Flex
            gap='4px'
            justify='flex-end'
            opacity={0}
            _groupHover={{ opacity: 1 }}
            transition='opacity 0.12s ease'
        >
            {actions.map((action) => (
                <Tooltip key={action.label} label={action.label} hasArrow placement='top' fontSize='xs'>
                    <IconButton
                        aria-label={action.label}
                        icon={<action.icon size='16px' />}
                        size='xs'
                        variant='ghost'
                        colorScheme={action.colorScheme}
                        _hover={{ bg: hoverBg }}
                        onClick={(e) => {
                            e.stopPropagation();
                            action.onClick();
                        }}
                    />
                </Tooltip>
            ))}
        </Flex>
    );
}
